/** Public runtime configuration needed by browser Sentry initialization. */
export type SentryRuntimeConfig = {
  dsn?: string
  environment?: string
  telemetryEnabled?: string
  tracePropagationTargets?: string[]
}

export const SENTRY_RUNTIME_CONFIG_KEY = '__CARGOKING_SENTRY_RUNTIME_CONFIG__'
export const SENTRY_RUNTIME_CONFIG_EVENT = 'cargoking:sentry-runtime-config'

const escapeJsonForScript = (value: string): string =>
  value
    .replaceAll('<', '\\u003c')
    .replaceAll('>', '\\u003e')
    .replaceAll('&', '\\u0026')
    .replaceAll('\u2028', '\\u2028')
    .replaceAll('\u2029', '\\u2029')

export const serializeSentryRuntimeConfig = (config: SentryRuntimeConfig): string =>
  escapeJsonForScript(
    JSON.stringify({
      dsn: config.dsn,
      environment: config.environment,
      telemetryEnabled: config.telemetryEnabled,
      tracePropagationTargets: config.tracePropagationTargets,
    }),
  )

export const getSentryRuntimeConfigScript = (config: SentryRuntimeConfig): string =>
  `window.${SENTRY_RUNTIME_CONFIG_KEY}=${serializeSentryRuntimeConfig(config)};window.dispatchEvent(new Event('${SENTRY_RUNTIME_CONFIG_EVENT}'));`

type RuntimeWindow = Window & {
  [SENTRY_RUNTIME_CONFIG_KEY]?: SentryRuntimeConfig
}

const getRuntimeWindow = (): RuntimeWindow | undefined =>
  // SAFETY: the typeof guard runs before accessing the browser-only global.
  typeof window === 'undefined' ? undefined : (window as RuntimeWindow)

export const initializeSentryFromRuntimeConfig = (
  initialize: (_config: SentryRuntimeConfig) => void,
): void => {
  const runtimeWindow = getRuntimeWindow()
  if (!runtimeWindow) return

  let initialized = false
  const run = (config: SentryRuntimeConfig | undefined) => {
    if (initialized || !config) return
    initialized = true
    runtimeWindow.removeEventListener(SENTRY_RUNTIME_CONFIG_EVENT, onConfig)
    try {
      initialize(config)
    } catch {
      console.warn('Sentry runtime initialization was skipped.')
    }
  }
  const onConfig = () => run(runtimeWindow[SENTRY_RUNTIME_CONFIG_KEY])

  runtimeWindow.addEventListener(SENTRY_RUNTIME_CONFIG_EVENT, onConfig)
  run(runtimeWindow[SENTRY_RUNTIME_CONFIG_KEY])
}
